import {
  toWSDateString,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Account, PayStatement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { PAYROLL_CURRENCY } from "../../../PayrollRuns/constants";
import { parsePayrollAmount } from "../../../PayrollRuns/utils";
import { formatPayerName } from "../../utils";

const EMPTY_VALUE = "—";

type Props = {
  paystubs: PayStatement[];
  /**
   * Payer accounts by id, for the employer-name column. The pay-statement read
   * is flat (each row carries only `payerAccountId`), so the caller resolves the
   * accounts — see `useQueryPayerAccounts`.
   */
  payerAccountsById?: Map<string, Account>;
  loading?: boolean;
  onDownload?: (id: string) => void;
  onRowOpen?: (id: string) => void;
};

const getColumns = (
  payerAccountsById?: Map<string, Account>
): WSTableColumn<PayStatement>[] => [
  {
    config: { header: { text: "Date" }, gridTemplateSizeMax: "1fr" },
    renderFunction: ({ data }) => (
      <WSTableCell
        text={
          data.payPeriodEndDate
            ? toWSDateString(data.payPeriodEndDate, "monthDayYear")
            : EMPTY_VALUE
        }
      />
    )
  },
  {
    config: { header: { text: "Paid date" }, gridTemplateSizeMax: "1fr" },
    renderFunction: ({ data }) => (
      <WSTableCell
        text={
          data.payDate
            ? toWSDateString(data.payDate, "monthDayYear")
            : EMPTY_VALUE
        }
      />
    )
  },
  {
    config: { header: { text: "Employer name" }, gridTemplateSizeMax: "1fr" },
    renderFunction: ({ data }) => (
      <WSTableCell
        text={formatPayerName(
          payerAccountsById?.get(data.payerAccountId ?? "")
        )}
      />
    )
  },
  {
    config: { header: { text: "Net pay" }, gridTemplateSizeMax: "1fr" },
    renderFunction: ({ data }) => (
      <WSTableCell
        text={formatMoney(
          parsePayrollAmount(data.amountDetails?.netPay),
          PAYROLL_CURRENCY
        )}
      />
    )
  },
  {
    config: { header: { text: "Gross pay" }, gridTemplateSizeMax: "1fr" },
    renderFunction: ({ data }) => (
      <WSTableCell
        text={formatMoney(
          parsePayrollAmount(data.amountDetails?.grossPay),
          PAYROLL_CURRENCY
        )}
      />
    )
  }
];

export const PaystubHistoryTable: React.FC<Props> = ({
  paystubs,
  payerAccountsById,
  loading,
  onDownload,
  onRowOpen
}) => {
  const tableData: WSTableItem<PayStatement>[] = paystubs.map(paystub => ({
    id: paystub.id ?? "",
    data: paystub
  }));

  return (
    <WSTable<PayStatement>
      columns={getColumns(payerAccountsById)}
      loading={loading}
      rowActions={
        onDownload
          ? item => [
              {
                icon: "download",
                text: "Download",
                onClick: () => onDownload(item.id)
              }
            ]
          : undefined
      }
      tableData={tableData}
      onRowClick={item => onRowOpen?.(item.id)}
    />
  );
};
